import React, { useState, useCallback, useEffect, useRef } from 'react'
import supabase from '@/supabaseClient'
import {
  SteamGame,
  SteamProfile,
  SteamAuthResponse,
  SteamIntegrationReturn,
  AppUser
} from './types'
import { createMockSteamId, isSteamMockEnabled } from './steamMock'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'
const BASE_URL =
  import.meta.env.MODE === 'development'
    ? 'http://localhost:5173'
    : import.meta.env.VITE_BACKLOG_EXPLORER_URL
const STEAM_FAST_QUERY = 'fast=1'
const STEAM_POLL_INTERVAL_MS = 15000
const STEAM_POLL_MAX_ATTEMPTS = 12
const STEAM_POLL_MAX_NO_CHANGE = 3

export const useSteamIntegration = (
  user: AppUser | null,
  setUser: React.Dispatch<React.SetStateAction<AppUser | null>>
): SteamIntegrationReturn => {
  const mockEnabled = isSteamMockEnabled()
  const [steamProfile, setSteamProfile] = useState<SteamProfile | null>(null)
  const [steamGames, setSteamGames] = useState<SteamGame[]>([])
  const [loadingGames, setLoadingGames] = useState(false)
  const [selectedGames, setSelectedGames] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const pollTimerRef = useRef<number | null>(null)
  const pollAttemptsRef = useRef(0)
  const pollNoChangeRef = useRef(0)
  const pollInFlightRef = useRef(false)
  const pollLastEnrichedRef = useRef(0)
  const hydrateAttemptedRef = useRef(false)
  const openIdHandledRef = useRef(false)

  useEffect(() => {
    hydrateAttemptedRef.current = false
  }, [user?.id])

  const clearPolling = useCallback(() => {
    if (pollTimerRef.current) {
      window.clearInterval(pollTimerRef.current)
      pollTimerRef.current = null
    }
    pollAttemptsRef.current = 0
    pollNoChangeRef.current = 0
    pollInFlightRef.current = false
  }, [])

  useEffect(() => {
    return () => {
      clearPolling()
    }
  }, [clearPolling])

  const countEnriched = useCallback((games: SteamGame[]) => {
    return games.reduce((total, game) => {
      if (game.igdb_id || game.background_image || game.description) {
        return total + 1
      }
      return total
    }, 0)
  }, [])

  const fetchSteamGames = useCallback(
    async (steamId: string) => {
      const {
        data: { session },
      } = await supabase.auth.getSession()
      const accessToken = session?.access_token
      if (!accessToken) {
        throw new Error('Missing session. Please log in again.')
      }
      const controller = new AbortController()
      const timeoutId = window.setTimeout(() => {
        controller.abort()
      }, 20000)
      const gamesResponse = await fetch(
        `${API_BASE_URL}/api/steam/games/${steamId}?${STEAM_FAST_QUERY}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          signal: controller.signal,
        }
      )
      window.clearTimeout(timeoutId)
      if (!gamesResponse.ok) {
        const gamesError = await gamesResponse.json().catch(() => null)
        throw new Error(gamesError?.error || 'Failed to fetch Steam games')
      }
      return (await gamesResponse.json()) as SteamGame[]
    },
    [API_BASE_URL]
  )

  const startSteamPolling = useCallback(
    (steamId: string, initialGames: SteamGame[]) => {
      clearPolling()
      if (!initialGames.length) return
      pollLastEnrichedRef.current = countEnriched(initialGames)
      pollTimerRef.current = window.setInterval(async () => {
        if (pollInFlightRef.current) return
        if (pollAttemptsRef.current >= STEAM_POLL_MAX_ATTEMPTS) {
          clearPolling()
          return
        }
        pollInFlightRef.current = true
        try {
          const updatedGames = await fetchSteamGames(steamId)
          const enrichedCount = countEnriched(updatedGames)
          if (enrichedCount > pollLastEnrichedRef.current) {
            pollLastEnrichedRef.current = enrichedCount
            pollNoChangeRef.current = 0
            setSteamGames(updatedGames)
          } else {
            pollNoChangeRef.current += 1
          }
        } catch (pollError) {
          console.warn('Steam game refresh failed:', pollError)
          pollNoChangeRef.current += 1
        } finally {
          pollAttemptsRef.current += 1
          pollInFlightRef.current = false
        }
        if (
          pollAttemptsRef.current >= STEAM_POLL_MAX_ATTEMPTS ||
          pollNoChangeRef.current >= STEAM_POLL_MAX_NO_CHANGE
        ) {
          clearPolling()
        }
      }, STEAM_POLL_INTERVAL_MS)
    },
    [clearPolling, countEnriched, fetchSteamGames]
  )

  const clearOpenIdParams = useCallback(() => {
    try {
      const url = new URL(window.location.href)
      const params = new URLSearchParams(url.search)
      let changed = false
      for (const key of Array.from(params.keys())) {
        if (key.startsWith('openid.')) {
          params.delete(key)
          changed = true
        }
      }
      if (changed) {
        url.search = params.toString()
        window.history.replaceState({}, document.title, url.toString())
      }
    } catch {
      // ignore
    }
  }, [])
  const handleConnectToSteam = async () => {
    if (mockEnabled) {
      setLoadingGames(true)
      try {
        const {
          data: { user: currentUser },
        } = await supabase.auth.getUser()
        const seedValue =
          currentUser?.id || user?.id || user?.email || 'steam-mock'
        const steamId = createMockSteamId(seedValue)
        const mockProfile = {
          steamId,
          nickname: 'Mock Steam User',
          avatar: '',
          profileUrl: 'https://steamcommunity.com/id/mock',
        }

        setSteamProfile(mockProfile)

        if (currentUser?.id) {
          const { data: existingProfile, error: existingError } =
            await supabase
              .from('user_steam_profiles')
              .select('id')
              .eq('user_id', currentUser.id)
              .maybeSingle()

          if (existingError) throw existingError

          const payload = {
            user_id: currentUser.id,
            steam_id: steamId,
            steam_profile: mockProfile,
            steam_username: mockProfile.nickname,
            steam_avatar: mockProfile.avatar || null,
            steam_profile_url: mockProfile.profileUrl,
            updated_at: new Date().toISOString(),
          }

          const { error: persistError } = existingProfile
            ? await supabase
                .from('user_steam_profiles')
                .update(payload)
                .eq('user_id', currentUser.id)
            : await supabase.from('user_steam_profiles').insert(payload)

          if (persistError) {
            setError('Failed to persist Steam profile.')
            throw persistError
          }
        }

        setUser((prevUser) => {
          if (!prevUser) return null
          return {
            ...prevUser,
            steamId,
            nickname: mockProfile.nickname,
            avatar: mockProfile.avatar,
            profileUrl: mockProfile.profileUrl,
          }
        })

        const games = await fetchSteamGames(steamId)
        setSteamGames(games)
        startSteamPolling(steamId, games)
      } catch (mockError) {
        console.error('Mock Steam connect failed:', mockError)
        const message =
          mockError instanceof Error
            ? mockError.message
            : 'Failed to connect mock Steam'
        setError(message)
      } finally {
        setLoadingGames(false)
      }
      return
    }

    const params = {
      'openid.ns': 'http://specs.openid.net/auth/2.0',
      'openid.mode': 'checkid_setup',
      'openid.return_to': `${BASE_URL}/app/profile`,
      'openid.realm': BASE_URL,
      'openid.identity': 'http://specs.openid.net/auth/2.0/identifier_select',
      'openid.claimed_id': 'http://specs.openid.net/auth/2.0/identifier_select',
    }

    const steamLoginUrl = `https://steamcommunity.com/openid/login?${new URLSearchParams(
      params
    )}`
    window.location.href = steamLoginUrl
  }
  const handleOpenIDResponse = useCallback(async () => {
    if (mockEnabled) return
    if (openIdHandledRef.current) return
    openIdHandledRef.current = true
    setLoadingGames(true)
    try {
      const urlParams = new URLSearchParams(window.location.search)

      const controller = new AbortController()
      const timeoutId = window.setTimeout(() => {
        controller.abort()
      }, 15000)
      const response = await fetch(`${API_BASE_URL}/api/steam/verify-openid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          openidResponse: Object.fromEntries(urlParams.entries()),
        }),
        signal: controller.signal,
      })
      window.clearTimeout(timeoutId)

      const responseText = await response.text()
      let responseData: SteamAuthResponse | null = null
      try {
        responseData = responseText.trim()
          ? (JSON.parse(responseText) as SteamAuthResponse)
          : null
      } catch (jsonError) {
        console.error('Failed to parse Steam API response:', jsonError)
      }
      if (!response.ok) {
        const errorMessage =
          (responseData as { error?: string } | null)?.error ||
          responseText ||
          `Steam verification failed (${response.status})`
        throw new Error(errorMessage)
      }
      if (!responseData) {
        throw new Error('Empty response from Steam API')
      }

      setSteamProfile(responseData)

      if (responseData.steamId) {
        try {
          const {
            data: { user: currentUser },
          } = await supabase.auth.getUser()
          if (currentUser?.id) {
            const { data: existingProfile, error: existingError } =
              await supabase
                .from('user_steam_profiles')
                .select('id')
                .eq('user_id', currentUser.id)
                .maybeSingle()

            if (existingError) throw existingError

            const payload = {
              user_id: currentUser.id,
              steam_id: responseData.steamId,
              steam_profile: responseData,
              steam_username: responseData.nickname || null,
              steam_avatar: responseData.avatar || null,
              steam_profile_url: responseData.profileUrl || null,
              updated_at: new Date().toISOString(),
            }

            const { error: persistError } = existingProfile
              ? await supabase
                  .from('user_steam_profiles')
                  .update(payload)
                  .eq('user_id', currentUser.id)
              : await supabase.from('user_steam_profiles').insert(payload)

            if (persistError) {
              setError('Failed to persist Steam profile.')
              throw persistError
            }
          }
        } catch (profileError) {
          console.warn('Failed to persist Steam profile:', profileError)
        }

        setUser((prevUser) => {
          if (!prevUser) return null
          return {
            ...prevUser,
            steamId: responseData.steamId,
            nickname: responseData.nickname,
            avatar: responseData.avatar,
            profileUrl: responseData.profileUrl,
          }
        })

        const games = await fetchSteamGames(responseData.steamId)
        setSteamGames(games)
        startSteamPolling(responseData.steamId, games)
      }
    } catch (error) {
      console.error('Error handling OpenID response:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to handle OpenID response'
      setError(errorMessage)
    } finally {
      clearOpenIdParams()
      setLoadingGames(false) // ✅ Stop loading spinner
    }
  }, [API_BASE_URL, clearOpenIdParams, fetchSteamGames, mockEnabled, setUser, startSteamPolling])

  const hydrateSteamFromProfile = useCallback(async () => {
    if (mockEnabled) return
    if (!user?.id || loadingGames || steamGames.length > 0) return
    if (hydrateAttemptedRef.current) return
    hydrateAttemptedRef.current = true

    setLoadingGames(true)
    try {
      const { data: existingProfile, error: profileError } = await supabase
        .from('user_steam_profiles')
        .select(
          'steam_id,steam_profile,steam_username,steam_avatar,steam_profile_url'
        )
        .eq('user_id', user.id)
        .maybeSingle()

      if (profileError) throw profileError
      if (!existingProfile?.steam_id) return

      const profilePayload =
        existingProfile.steam_profile && Object.keys(existingProfile.steam_profile).length
          ? existingProfile.steam_profile
          : {
              steamId: existingProfile.steam_id,
              nickname: existingProfile.steam_username || '',
              avatar: existingProfile.steam_avatar || '',
              profileUrl: existingProfile.steam_profile_url || '',
            }

      setSteamProfile(profilePayload)

      setUser((prevUser) => {
        if (!prevUser) return null
        return {
          ...prevUser,
          steamId: existingProfile.steam_id,
          nickname: profilePayload.nickname,
          avatar: profilePayload.avatar,
          profileUrl: profilePayload.profileUrl,
        }
      })

      const games = await fetchSteamGames(existingProfile.steam_id)
      setSteamGames(games)
      startSteamPolling(existingProfile.steam_id, games)
    } catch (error) {
      console.error('Error hydrating Steam profile:', error)
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load Steam profile'
      setError(errorMessage)
    } finally {
      setLoadingGames(false)
    }
  }, [
    fetchSteamGames,
    loadingGames,
    mockEnabled,
    setError,
    setSteamProfile,
    setUser,
    startSteamPolling,
    steamGames.length,
    user?.id,
  ])
  const handleDisconnectSteam = async () => {
    if (!user?.id) {
      console.error('No user ID found for disconnecting Steam')
      return
    }

    try {
      clearPolling()
      const { error } = await supabase
        .from('user_steam_profiles')
        .delete()
        .eq('user_id', user.id)

      if (error) throw error

      setSteamGames([])
      setSelectedGames(new Set())

      setUser(user ? {
        ...user,
        steamId: undefined,
      } : null)
    } catch (error) {
      console.error('Error disconnecting Steam:', error)
      setError('Failed to disconnect Steam account')
    }
  }

  return {
    steamGames,
    steamProfile,
    loadingGames,
    selectedGames,
    setSelectedGames,
    handleConnectToSteam,
    handleOpenIDResponse,
    hydrateSteamFromProfile,
    handleDisconnectSteam,
    error,
    setError: (error: string | null) => setError(error),
  }
}
